import { GlobalVariables } from './../../services/globals';
import { Injectable } from '@angular/core';
import {
    ConnectorMetadataScope,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { GlobalVariables as shellGlobalVariables } from 'shell-models-lib';

@Injectable({
    providedIn: 'root'
})
export class LinkedServiceRegistryService {
    private readonly _linkedServiceConfigurations: ILinkedServiceTypeMetadata[] = [];

    constructor(private readonly _exposureControlService: ExposureControlService) { }

    public register(config: ILinkedServiceTypeMetadata): void {
        if (config.disabled) {
            return;
        }
        this._linkedServiceConfigurations.push(config);
    }

    public has(type: string): boolean {
        return this._linkedServiceConfigurations.find((e) => e.type === type) != null;
    }

    public getAllLinkedServiceConfigurations(): ILinkedServiceTypeMetadata[] {
        return this._linkedServiceConfigurations
            .filter((e) => !__IS_FABRIC_DPE__ || e.supportedInTrident)
            .sort((a, b) => (a.displayName > b.displayName ? 1 : -1));
    }

    public getAllVisibleLinkedServiceConfigurations(): ILinkedServiceTypeMetadata[] {
        return this._linkedServiceConfigurations
            .filter((e) => !this.hidedByEc(e.type))
            .filter((e) => (!__IS_FABRIC_DPE__ || e.supportedInTrident) && !e.notShowInTypeSelection)
            .sort((a, b) => (a.displayName > b.displayName ? 1 : -1));
    }

    // e.metadataScope == null means it's the primary metadata, otherwise, it's the metadata under a specific scope.
    // alias is not primary, but visiable
    public getAllPrimaryLinkedServiceConfigurations(): ILinkedServiceTypeMetadata[] {
        return this._linkedServiceConfigurations
            .filter((e) => (!__IS_FABRIC_DPE__ || e.supportedInTrident) && e.metadataScope == null)
            .sort((a, b) => (a.displayName > b.displayName ? 1 : -1));
    }

    /**
     * getLinkedServiceConfiguration
     * @param lsType linked service type
     * @param metadataScope
     * Note that linkedservices under different scope may equip with their own configurations, if specify metadataScope, it returns linkedservice configuration under the specific metadataScope
     */
    public getLinkedServiceConfiguration(
        lsType: string,
        metadataScope?: ConnectorMetadataScope
    ): ILinkedServiceTypeMetadata {
        const metadatas = this._linkedServiceConfigurations.filter((config) => config.type === lsType);
        if (metadatas.length > 0) {
            return metadataScope == null
                ? metadatas.find((config) => config.metadataScope == null) || metadatas[0]
                : metadatas.find((config) => config.metadataScope === metadataScope) || metadatas[0];
        } else {
            throw new Error(`${lsType} not found in linked-service-registery`);
        }
    }

    public hidedByEc(lsType: string): boolean {
        if (GlobalVariables.showAllFeatures) {
            return false;
        }
        switch (lsType) {
            case adfModelTypeMap.kusto.linkedServiceType:
                return !GlobalVariables.showKustoConnector;
            case adfModelTypeMap.azureDataLakeStoreCosmosStructuredStream.linkedServiceType:
            case adfModelTypeMap.azureDataLakeStoreGen2CosmosStructuredStream.linkedServiceType:
                return !this._exposureControlService.enableADLSCosmosDataset;
            case adfModelTypeMap.adobeExperiencePlatform.linkedServiceType:
                return !GlobalVariables.enableAepInternal;
            case adfModelTypeMap.amlService.linkedServiceType:
                return !__IS_FABRIC_DPE__ && !GlobalVariables.isPostGAFactory;
            case adfModelTypeMap.adobeIntegration.linkedServiceType:
                return !GlobalVariables.enableAdobeIntegration;
            case adfModelTypeMap.genericCData.linkedServiceType:
                return !GlobalVariables.enableGenericCDataConnector;
            case adfModelTypeMap.sharePointOnlineFile.linkedServiceType:
                return !(
                    // fabric data pipeline env
                    __IS_FABRIC_DPE__ ||
                    // adf env
                    this._exposureControlService.enableSharePointOnlineFile
                );
            case adfModelTypeMap.workday.linkedServiceType:
                return !GlobalVariables.isWorkdayEnabled;
            case adfModelTypeMap.eventHubs.linkedServiceType:
                return !(GlobalVariables.enableEventHubsMDF || GlobalVariables.enableEventHubsCDC);
            case adfModelTypeMap.kafka.linkedServiceType:
                return !GlobalVariables.enableKafkaCDC;
            case adfModelTypeMap.sapOData.linkedServiceType:
                return !this._exposureControlService.enableSAPOData;
            case adfModelTypeMap.webActivity.linkedServiceType:
                return !GlobalVariables.enableNewWebActivityLS;
            case adfModelTypeMap.microsoftTeams.linkedServiceType:
                return !GlobalVariables.microsoftTeams;
            case adfModelTypeMap.office365Email.linkedServiceType:
                return !GlobalVariables.office365Email;
            case adfModelTypeMap.mountedRelationalDatabase.linkedServiceType:
                return !GlobalVariables.enableOpenMirrorSink;
            case adfModelTypeMap.tridentSparkJobDefinition.linkedServiceType:
                return !(__IS_FABRIC_DPE__ && this._exposureControlService.UseTokenServiceInSJDActivity);
            case adfModelTypeMap.fabricNotebook.linkedServiceType:
                return !(__IS_FABRIC_DPE__ && this._exposureControlService.UseTokenServiceInNotebookActivity);
            case adfModelTypeMap.fabricUserDataFunctions.linkedServiceType:
                return !(__IS_FABRIC_DPE__ && this._exposureControlService.UseTokenServiceInDFActivity);
            case adfModelTypeMap.fabricDataflow.linkedServiceType:
                return !(
                    __IS_FABRIC_DPE__ &&
                    GlobalVariables.useDataflowConnection &&
                    this._exposureControlService.UseTokenServiceInDataFlowActivity
                );
            case adfModelTypeMap.fabricWorkspace.linkedServiceType:
            case adfModelTypeMap.fabricDataPipelines.linkedServiceType:
            case adfModelTypeMap.azureDataFactory.linkedServiceType:
            case adfModelTypeMap.powerBIDatasets.linkedServiceType:
            case adfModelTypeMap.fabricSql.linkedServiceType:
            case adfModelTypeMap.databricks.linkedServiceType:
                return !__IS_FABRIC_DPE__;
            case adfModelTypeMap.sqlAnalyticsEndpoint.linkedServiceType:
                return !__IS_FABRIC_DPE__ || !shellGlobalVariables.enableSQLAnalyticsEndpoint;
            case adfModelTypeMap.fabricMaterializedLakehouseView.linkedServiceType:
                return !__IS_FABRIC_DPE__ || !GlobalVariables.enableRefreshMaterializedLakeViewActivity;
            default:
                return false;
        }
    }

    public notVisible(lsType: string): boolean {
        try {
            return this.getLinkedServiceConfiguration(lsType).notShowInTypeSelection;
        } catch {
            return true;
        }
    }
}
